import React, { useState, useEffect } from 'react';
import { DatePicker, Button, Menu, Row, Col, Table, Empty, Select } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import Dashheader from '../Dashboard/Dashheader/Dashheader';
import api from '../../api.js';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';

const { RangePicker } = DatePicker;

const ReportBookedFlat = ({ refreshTrigger }) => { // refreshTrigger prop for overall table data refresh
  const navigate = useNavigate();
  const [dates, setDates] = useState([dayjs().startOf('month'), dayjs()]);
  const [towerList, setTowerList] = useState([]);
  const [selectedTowerName, setSelectedTowerName] = useState("ALL");

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const columns = [
    { title: 'Booking ID', dataIndex: 'bookedNo', key: 'bookedNo' },
    { title: 'Flat No', dataIndex: 'flatNo', key: 'flatNo' },
    { title: 'Tower', dataIndex: 'towerName', key: 'towerName' },
    { title: 'Block', dataIndex: 'block', key: 'block' },
    { title: 'Floor', dataIndex: 'floor', key: 'floor' },
    { title: 'Unit Type', dataIndex: 'unitType', key: 'unitType' },
    { title: 'Facing', dataIndex: 'facing', key: 'facing' },
    { title: 'Sq Feet', dataIndex: 'sqFeet', key: 'sqFeet', render: (sqFeet) => sqFeet ? sqFeet.toLocaleString('en-IN') : 'N/A' },
    { title: 'Owner Name', dataIndex: 'userName', key: 'userName' },
    { title: 'Phone', dataIndex: 'userPhone', key: 'userPhone' },
    {
      title: 'Booking Date',
      dataIndex: 'bookedOn',
      key: 'bookedOn',
      render: (date) => date ? dayjs(date).format('DD-MM-YYYY') : 'N/A',
    },
    {
      title: 'Total Amount (₹)',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      render: (amount) => amount !== null && amount !== undefined ? amount.toLocaleString('en-IN') : 'N/A',
    },
    {
      title: 'Special Feature',
      dataIndex: 'specialFeature',
      key: 'specialFeature',
    },
    {
      title: 'Special Feature Price (₹)',
      dataIndex: 'specialFeaturePrice',
      key: 'specialFeaturePrice',
      render: (price) => price !== null && price !== undefined ? price.toLocaleString('en-IN') : 'N/A',
    },
    { title: 'Parking Slot', dataIndex: 'parkingSlot', key: 'parkingSlot' },
    {
      title: 'Parking Charges (₹)',
      dataIndex: 'parkingCharges',
      key: 'parkingCharges',
      render: (charges) => charges !== null && charges !== undefined ? charges.toLocaleString('en-IN') : 'N/A',
    },
    {
      title: 'Total Payments (₹)',
      key: 'totalPayments',
      render: (_, record) => {
        const totalPaid = record.paymentIds?.reduce((sum, payment) => sum + (payment.amount || 0), 0) || 0;
        return totalPaid.toLocaleString('en-IN');
      },
    },
    // {
    //   title: 'Booking PDF',
    //   dataIndex: 'url',
    //   key: 'url',
    //   render: (url) => url ? (
    //     // *** IMPORTANT CHANGE HERE: Appending a cache-busting timestamp ***
    //     <a href={`${url}?cachebust=${new Date().getTime()}`} target="_blank" rel="noopener noreferrer">
    //       Download
    //     </a>
    //   ) : 'N/A',
    // },
    {
  title: 'Booking PDF',
  key: 'bookingPdf',
  render: (_, record) => (
    <a
      onClick={() => navigate(`/bookedflatinvoice/${record.bookedNo}`)}
      style={{ cursor: 'pointer' }}
    >
      Download
    </a>
  ),
}




  ];

  // fetchData will now run when dates, selectedTowerName OR refreshTrigger changes
  useEffect(() => {
    if (dates[0] && dates[1]) {
      fetchData();
    }
  }, [dates, selectedTowerName, refreshTrigger]);

  const fetchData = async () => {
    setLoading(true);
    const params = {
      startDate: dates[0]?.format('YYYY-MM-DD'),
      endDate: dates[1]?.format('YYYY-MM-DD'),
      towerName: selectedTowerName
    };

    try {
      console.log('Sending params:', params);
      const response = await api.getBookedFlat(params);
      console.log('API Response:', response);

      if (response.success && Array.isArray(response.data)) {
        setData(response.data);
      } else {
        setData([]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!data || data.length === 0) {
      return;
    }

    const exportData = data.map(item => {
      const totalPaid = item.paymentIds?.reduce((sum, payment) => sum + (payment.amount || 0), 0) || 0;

      return {
        bookedNo: item.bookedNo,
        flatNo: item.flatNo,
        towerName: item.towerName,
        block: item.block,
        floor: item.floor,
        unitType: item.unitType,
        facing: item.facing,
        sqFeet: item.sqFeet,
        userName: item.userName,
        userPhone: item.userPhone,
        bookedOn: item.bookedOn ? new Date(item.bookedOn).toLocaleDateString() : 'N/A',
        totalAmount: item.totalAmount,
        specialFeature: item.specialFeature,
        specialFeaturePrice: item.specialFeaturePrice,
        parkingSlot: item.parkingSlot,
        parkingCharges: item.parkingCharges,
        totalPayments: totalPaid,
        remarks: item.remarks
      };
    });

    const headers = Object.keys(exportData[0] || {});
    const capitalHeaders = headers.map(h =>
      h.replace(/([A-Z])/g, ' $1')
       .replace(/^./, c => c.toUpperCase())
       .toUpperCase()
    );

    const separatorRow = headers.map(() => '----------------');
    const sheetData = [capitalHeaders, separatorRow, ...exportData.map(row => headers.map(h => row[h]))];
    const worksheet = XLSX.utils.aoa_to_sheet(sheetData);

    headers.forEach((header, idx) => {
      const cellAddress = XLSX.utils.encode_cell({ r: 0, c: idx });
      if (!worksheet[cellAddress]) worksheet[cellAddress] = { t: 's', v: capitalHeaders[idx] };
      worksheet[cellAddress].s = {
        font: { bold: true, sz: 14, name: 'Arial Black' },
        alignment: { vertical: "center", horizontal: "center" }
      };
    });

    headers.forEach((header, idx) => {
      const cellAddress = XLSX.utils.encode_cell({ r: 1, c: idx });
      if (worksheet[cellAddress]) {
        worksheet[cellAddress].s = {
          font: { bold: false, sz: 12 },
          alignment: { vertical: "center", horizontal: "center" }
        };
      }
    });

    for (let r = 2; r < sheetData.length; r++) {
      for (let c = 0; c < headers.length; c++) {
        const cellAddress = XLSX.utils.encode_cell({ r, c });
        if (worksheet[cellAddress]) {
          worksheet[cellAddress].s = {
            font: { bold: false, sz: 12 },
            alignment: { vertical: "center", horizontal: "center" }
          };
        }
      }
    }

    worksheet['!cols'] = headers.map(() => ({ wch: 20 }));
    worksheet['!rows'] = [{ hpt: 32 }, { hpt: 10 }];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'BookedFlats');
    XLSX.writeFile(workbook, 'BookedFlatsReport.xlsx');
  };

  useEffect(() => {
    getTowerNames();
  }, []);

  const getTowerNames = async () => {
    try {
      const response = await api.getTowerNames();
      console.log(response, "response");

      if (response.data.success && Array.isArray(response.data.data)) {
        const names = [
          { label: 'ALL', value: 'ALL' },
          ...response.data.data.map(tower => ({
            label: tower.name,
            value: tower.name,
          })),
        ];
        setTowerList(names);
        console.log(names, "tower names");
        setSelectedTowerName('ALL');
      } else {
        console.error('Failed to fetch tower names');
      }
    } catch (error) {
      console.error('Error fetching tower names:', error);
    }
  };

  return (
    <>
      <Dashheader />
      <div style={{ padding: '20px' }}>
        <Button onClick={() => navigate('/dashboard/reports')} style={{ marginBottom: 16, background: '#1890ff', color: '#fff', border: 'none' }}>
          <ArrowLeftOutlined style={{ marginRight: 6 }} />
          Back To reports
        </Button>
        <h1 className="text-center">Booked Flat History</h1>

        <Row gutter={[16, 16]} justify="center">
          <Col xs={24} sm={8}>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
              <div style={{ width: '55%' }}>
                <span style={{ fontSize: 14, color: '#555', fontWeight: 500 }}>Start Date</span>
                <DatePicker
  value={dates[0]}
  onChange={date => setDates([date, dates[1]])}
  style={{ width: '100%', height: 40, fontSize: 16 }}
  placeholder="Start date"
  size="large"
  format="DD-MM-YYYY"
/>
              </div>
              <div style={{ width: '55%' }}>
                <span style={{ fontSize: 14, color: '#555', fontWeight: 500 }}>End Date</span>
                <DatePicker
  value={dates[1]}
  onChange={date => setDates([dates[0], date])}
  style={{ width: '100%', height: 40, fontSize: 16 }}
  placeholder="End date"
  size="large"
  format="DD-MM-YYYY"
/>
              </div>
            </div>
          </Col>
          <Col xs={24} sm={8}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span style={{ fontSize: 14, color: '#555', fontWeight: 500, marginBottom: 4 }}>Project Name</span>
              <Select
                style={{ width: '100%', height: 44, fontSize: 16 }}
                value={selectedTowerName}
                onChange={(value) => setSelectedTowerName(value)}
                options={towerList}
                placeholder="Select Tower"
                size="large"
              />
            </div>
          </Col>
          <Col xs={24} sm={8} style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
            <Button type="primary" onClick={handleDownload} style={{ width: '50%' }}>
              Download Report
            </Button>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={data}
          rowKey="_id"
          loading={loading}
          pagination={{ pageSize: 10, showSizeChanger: false }}
          locale={{
            emptyText: <Empty description="No booked flats found" />,
          }}
          style={{ marginTop: '20px' }}
          scroll={{ x: 1500 }}
        />
      </div>
    </>
  );
};

export default ReportBookedFlat;
